import matchCondition, { resolvePath } from './matchCondition.js'

/**
 * Generic referential-integrity guard: "does anything still point at this record?"
 *
 * This is the reusable primitive for delete guards. `emptyCollection` and
 * `emptySubCollection` are thin aliases over it, so existing configs keep working.
 *
 * Config (under `condition`):
 *
 *   type          'collection' (default) | 'subCollection'
 *   collection    endpoint of the referencing entity            e.g. 'location'
 *   subCollection endpoint of the sub-entity (type subCollection) e.g. 'itemLocation'
 *   parentFrom    path to the parent id for subCollection scans  (default 'id')
 *   match         { targetField: 'pathOnThisRecord' }  → indexed server-side equality
 *   filter        { targetField: literal | [literals] } → indexed server-side constant
 *   where         [condition]  ALL must match for a row to count as a blocking reference
 *   ignore        [condition]  a row matching ANY of these is not a blocking reference
 *   via           { collection, match, select } — resolve a set of descendant keys first,
 *                 exposed to `where` as `valueFrom: '_via'`
 *
 * `via` is what lets a parent guard ask "is anything referencing anything BELOW me?"
 * in two bounded queries instead of one per descendant. A rack blocks its own delete
 * when an open order targets any of its positions:
 *
 *   via:   { collection: 'location', match: { rackId: 'id' }, select: 'id' }
 *   where: [{ field: 'lines[].fromLocationId', operator: 'containsAny', valueFrom: '_via' }]
 *
 * `match`/`filter` are pushed down to the database (cheap, indexed). `where`/`ignore`
 * are evaluated per row via matchCondition, which is what lets a guard ask
 * "…and only if it actually holds inventory" without a bespoke query.
 *
 * When no row-level predicate is needed the guard hits `<collection>/count`, so the
 * common case costs one indexed COUNT rather than pulling the rows.
 *
 * Fails CLOSED: if the reference check cannot be completed, the delete is blocked.
 * A guard that silently fails open would orphan records, which is the exact failure
 * mode this system exists to prevent.
 */

// Legacy shape from the original emptyCollection: filter: { field, value } where
// `value` is a path on the record being deleted, not a literal.
const isLegacyFilter = (filter) =>
    !!filter && typeof filter === 'object' && 'field' in filter && 'value' in filter

const buildFilterTokens = (condition, data) => {
    const tokens = []
    let hasDynamicMatch = false
    let resolvedAll = true

    const pushDynamic = (field, sourcePath) => {
        hasDynamicMatch = true
        const value = resolvePath(data, sourcePath)
        if (value === undefined || value === null || value === '') {
            resolvedAll = false
            return
        }
        tokens.push(`${field}:${value}`)
    }

    if (isLegacyFilter(condition.filter)) {
        pushDynamic(condition.filter.field, condition.filter.value)
    } else {
        Object.entries(condition.filter || {}).forEach(([field, value]) => {
            tokens.push(`${field}:${Array.isArray(value) ? value.join(',') : value}`)
        })
    }

    Object.entries(condition.match || {}).forEach(([field, sourcePath]) => pushDynamic(field, sourcePath))

    return { tokens, hasDynamicMatch, resolvedAll }
}

const buildEndpoint = (condition, data, { tokens, useCount }) => {
    const query = []
    if (tokens.length) query.push(`filter=${tokens.join(';')}`)

    if (condition.type === 'subCollection') {
        const parentId = resolvePath(data, condition.parentFrom || 'id')
        if (parentId === undefined || parentId === null || parentId === '') return null
        query.push('noPagination=true')
        return `${condition.collection}/${parentId}/${condition.subCollection}?${query.join('&')}`
    }

    if (useCount) return `${condition.collection}/count${query.length ? `?${query.join('&')}` : ''}`

    query.push('noPagination=true')
    return `${condition.collection}?${query.join('&')}`
}

// Resolve the descendant key set for a `via` clause.
const resolveVia = async (via, data, getData) => {
    const { tokens, hasDynamicMatch, resolvedAll } = buildFilterTokens(via, data)
    if (hasDynamicMatch && !resolvedAll) return { failed: false, values: [] }

    const query = tokens.length ? `filter=${tokens.join(';')}&noPagination=true` : 'noPagination=true'
    const endpoint = `${via.collection}?${query}`

    let fetched
    try {
        fetched = await getData(endpoint)
    } catch (err) {
        console.error(`<noReferences> via lookup failed for '${endpoint}':`, err?.message || err)
        return { failed: true, values: [] }
    }

    if (!Array.isArray(fetched?.data)) {
        console.error(`<noReferences> no usable rows from via '${endpoint}'`)
        return { failed: true, values: [] }
    }

    const values = fetched.data
        .map(row => resolvePath(row, via.select || 'id'))
        .filter(v => v !== undefined && v !== null && v !== '')

    return { failed: false, values }
}

const noReferences = async (target, condition, data, getData) => {
    if (!condition?.collection) {
        console.warn('<noReferences> condition.collection is required')
        return { valid: false }
    }

    const { where = [], ignore = [] } = condition
    const { tokens, hasDynamicMatch, resolvedAll } = buildFilterTokens(condition, data)

    // A dynamic match that resolved to nothing means this record has no key to be
    // referenced by (e.g. a rack with no id). Running the query anyway would scan
    // the whole collection and block every delete, so treat it as "no references".
    if (hasDynamicMatch && !resolvedAll) return { valid: true }

    // Optional descendant-key pre-resolution. The resolved set lands on the source
    // record as `_via` so `where` conditions can match against it.
    let source = data
    if (condition.via) {
        const via = await resolveVia(condition.via, data, getData)
        if (via.failed) return { valid: false }
        // No descendants means nothing downstream can be referenced.
        if (!via.values.length) return { valid: true }
        source = { ...data, _via: via.values }
    }

    // Row-level predicates need the rows themselves; otherwise COUNT is enough.
    const useCount = condition.type !== 'subCollection' && where.length === 0 && ignore.length === 0
    const endpoint = buildEndpoint(condition, data, { tokens, useCount })
    if (!endpoint) return { valid: true }

    let fetched
    try {
        fetched = await getData(endpoint)
    } catch (err) {
        console.error(`<noReferences> lookup failed for '${endpoint}':`, err?.message || err)
        return { valid: false }
    }

    if (useCount) {
        const count = Number(fetched?.count)
        if (!Number.isFinite(count)) {
            console.error(`<noReferences> no usable count from '${endpoint}'`)
            return { valid: false }
        }
        return count === 0 ? { valid: true } : { valid: false, count }
    }

    const rows = fetched?.data
    if (!Array.isArray(rows)) {
        console.error(`<noReferences> no usable rows from '${endpoint}'`)
        return { valid: false }
    }

    const blocking = rows.filter(row => {
        if (ignore.length && ignore.some(c => matchCondition(row, c, source))) return false
        if (where.length && !where.every(c => matchCondition(row, c, source))) return false
        return true
    })

    return blocking.length === 0 ? { valid: true } : { valid: false, count: blocking.length }
}

export default noReferences
